<template>
  <!-- The mobile stand-in for the top bar, which is hidden below lg: currency, language and support,
       each folding open in place. -->
  <div class="grid gap-2">
    <Disclosure v-if="brandconfig.CURRENCY_OPTION && !hideCurrency" v-slot="{ open }" as="div">
      <DisclosureButton class="flex w-full items-center justify-between gap-3 py-2 text-base font-medium">
        <span class="flex items-center gap-3">
          <span class="w-5 text-center font-semibold">{{ currencySymbol }}</span>
          {{ userStore.selectedCurrency }}
        </span>
        <ChevronDownIcon class="size-5 flex-none transition-transform" :class="open && 'rotate-180'" />
      </DisclosureButton>
      <DisclosurePanel class="grid pb-2 ps-8">
        <button
          v-for="currency of popularFxRates()"
          :key="currency[0]"
          type="button"
          class="flex items-center justify-between gap-3 py-2 text-start"
          :disabled="currency[0] === userStore.selectedCurrency"
          @click="selectCurrency(currency[0])">
          <span>{{ currency[2] ? currency[2] : currency[0] }}</span>
          <CheckIcon v-if="currency[0] === userStore.selectedCurrency" class="size-5 flex-none" />
        </button>
        <button v-if="!showAll" type="button" class="py-2 text-start text-muted-foreground" @click="showAll = true">
          {{ $t("Show all currencies") }}...
        </button>
      </DisclosurePanel>
    </Disclosure>

    <Disclosure v-if="locales && brandconfig.lang?.length > 1" v-slot="{ open }" as="div">
      <DisclosureButton class="flex w-full items-center justify-between gap-3 py-2 text-base font-medium">
        <span class="flex items-center gap-3">
          <LanguageIcon class="size-5 flex-none" />
          {{ langCode }}
        </span>
        <ChevronDownIcon class="size-5 flex-none transition-transform" :class="open && 'rotate-180'" />
      </DisclosureButton>
      <DisclosurePanel class="grid pb-2 ps-8">
        <button
          v-for="option of locales"
          :key="option"
          type="button"
          class="flex items-center justify-between gap-3 py-2 text-start"
          @click="switchLocale(option)">
          <span class="flex items-center gap-2">
            {{ LOCALES[option]?.title }}
          </span>
          <CheckIcon v-if="locale === option" class="size-5 flex-none" />
        </button>
      </DisclosurePanel>
    </Disclosure>

    <Disclosure v-slot="{ open }" as="div">
      <DisclosureButton class="flex w-full items-center justify-between gap-3 py-2 text-base font-medium">
        {{ $t("Support") }}
        <ChevronDownIcon class="size-5 flex-none transition-transform" :class="open && 'rotate-180'" />
      </DisclosureButton>
      <DisclosurePanel class="grid pb-2">
        <button type="button" class="flex items-center gap-3 py-2 text-start" @click="emit('contact')">
          <EnvelopeIcon class="size-5 flex-none" />
          {{ $t("Contact support") }}
        </button>
        <RouterLink to="/FAQ" class="flex items-center gap-3 py-2">
          <QuestionMarkCircleIcon class="size-5 flex-none" />
          {{ $t("FAQ") }}
        </RouterLink>
      </DisclosurePanel>
    </Disclosure>

    <CurrencyChangeModal :open="showModal" :currency="selectedCurrency" @hide-modal="showModal = false" />
  </div>
</template>

<script setup lang="ts">
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { CheckIcon, ChevronDownIcon, EnvelopeIcon, QuestionMarkCircleIcon } from "@heroicons/vue/24/outline";
import { RouterLink } from "vue-router";
import { LanguageIcon } from "@xs2event/js-lib/icons";
import brandconfig from "@/brandconfig";
import { useUserStore } from "@/store/user";
import CurrencyChangeModal from "./CurrencyChangeModal.vue";
import { LOCALES, locales } from "./locales.js";
import { useCurrencySelect } from "./useCurrencySelect";
import { useLanguageSwitch } from "./useLanguageSwitch";

// Contact support opens the ContactForm the mobile menu already hosts.
const emit = defineEmits<{ contact: [] }>();

const { hideCurrency = false } = defineProps<{ hideCurrency?: boolean }>();

const userStore = useUserStore();
const { showModal, selectedCurrency, showAll, popularFxRates, currencySymbol, selectCurrency } = useCurrencySelect();
const { locale, langCode, switchLocale } = useLanguageSwitch();
</script>
